import { useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { useTagsByName } from '../../../app/hooks/folders';
import { useEnsembleModelsQuery } from '../../../app/hooks/tags';
import { TimeUnit } from '../../utils/timeConversion';

import { DurationDropdown } from './DurationDropdown';
import { AlertDashboardPicker } from './alert-dashboard-picker';
import { SnoozeTitle } from './alert-modal';
import { AlertEmails } from './email-input';
import { customTextStyle, DisableWrapper, EditAlert, InfoCard } from './styles';
import { TeamsWebhookWrapper as TeamsWebhook } from './teams-webhook-picker';

import { Button, Dropdown, FormCheckbox, Icon, Switch, TextInput } from '@controlrooms/components';
import { ICONS } from '@controlrooms/constants';
import { useClickOutside } from '@controlrooms/hooks';
import { Alert, Ensemble } from '@controlrooms/models';

export enum AlertConditions {
  'ANOMALIES_DETECTED' = 'anomalies_detected',
  'IOW_LIMIT_EXCEEDED' = 'iow_limit_exceeded',
  'SPC_LIMIT_EXCEEDED' = 'spc_limit_exceeded',
}

enum AlertConditionsLabel {
  'ANOMALIES_DETECTED' = 'Anomalies detected',
  'IOW_LIMIT_EXCEEDED' = 'Limit exceeded',
  'SPC_LIMIT_EXCEEDED' = 'SPC Limit exceeded',
}

interface AlertModalFormProps {
  alertList?: Alert[];
  targetType: string;
  targetId: string | number;
  multipleEdit?: boolean;
}

const EditAlertV2 = styled(EditAlert)`
  padding: 0;
`;

const AlertConditionsWrapper = styled.div`
  display: flex;
  padding: 12px 10px 20px 10px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.alert.modal.sectionBorderColor};
  margin-top: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: left;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  font-family: 'Open Sans';
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 19px; /* 126.667% */
`;

const HeaderWithToggle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  font-family: 'Open Sans';
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 19px; /* 126.667% */
  margin-bottom: 10px;
`;

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AlertConditionsRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 60px;
  align-self: stretch;
`;

const AlertConditionsColumn = styled.div`
  display: flex;
  padding-left: 12px;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  flex: 1 0 0;
  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    margin: 0;
  }
  .alert-config {
    display: flex;
    flex-direction: column;
    align-self: stretch;
    gap: 10px;
    .checkbox {
      display: flex;
      align-items: center;
      gap: 4px;

      /* CR/13 Data */
      font-family: 'Open Sans';
      font-size: 13px;
      font-style: normal;
      font-weight: 400;
      line-height: 19px; /* 146.154% */
    }
    .section-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      align-self: stretch;
      width: 100%;
      height: 25px;
      padding-bottom: 5px;
      border-bottom: 1px solid ${({ theme }) => theme.alert.modal.buttonBorderColor};
      .ensemble {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 10px;
        > span {
          ${customTextStyle({})}
          color: ${({ theme }) => theme.alert.modal.durationTitleColor};
        }
      }
    }
    .alert-conditions {
      display: flex;
      padding-left: 20px;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
      align-self: stretch;
      .row {
        display: flex;
        align-items: flex-start;
        gap: 22px;
        .selector {
          margin-top: 25px;
          display: flex;
          width: 140px;
        }
        .group {
          display: flex;
          align-items: flex-start;
          gap: 5px;
        }
        .button-label {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          div.label {
            display: flex;
            align-items: flex-start;
            gap: 2px;
            height: 13px;

            /* CR/11 Data */
            font-family: 'Open Sans';
            font-size: 11px;
            font-style: normal;
            font-weight: 400;
            line-height: 13px; /* 118.182% */
          }
          .input-group {
            display: flex;
            align-items: center;
            gap: 4px;
            margin-top: 6px;
            .text-input {
              width: 46px;
              height: 24px;
              border: 1px solid var(--K-K-65, #b3b3b3);
            }
            input {
              height: 14px;
              padding: 5px 0;
            }
          }
        }
        .over {
          display: flex;
          margin-top: 25px;
          align-self: stretch;

          /* CR/11 Data */
          font-family: 'Open Sans';
          font-size: 11px;
          font-style: normal;
          font-weight: 400;
          line-height: 13px; /* 118.182% */
        }
      }
    }
  }
`;

const DeliverChannelsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  flex: 1 0 0;
  margin: 24px 0;
`;

const DeliverChannels = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
  padding-left: 20px;
  .row {
    display: flex;
    align-items: flex-start;
    gap: 60px;
    align-self: stretch;
  }
  .column {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex: 1 0 0;
  }
`;

export const MenuButton = styled(Button)`
  display: flex;
  height: 25px;
  min-width: 60px;
  max-width: 150px;
  padding: 0 8px;
  // flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.alert.modal.buttonBorderColor};
  background: ${({ theme }) => theme.alert.modal.actionButtonBgColor};

  /* Elevation/Light/Elevation 1 */
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.15);
  color: ${({ theme }) => theme.alert.modal.buttonTextColor};

  /* CR/11 Data */
  font-family: 'Open Sans';
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 13px; /* 118.182% */
  .menu-button-icon {
    width: unset;
    > svg {
      width: 8px;
      height: 8px;
    }
  }
`;

const DeliverChannelsHeader = styled(Header)`
  border-bottom: 1px solid ${({ theme }) => theme.alert.modal.buttonBorderColor};
`;

const ErrorMessage = styled.div`
  margin-top: 6px;
  // color: ${({ theme }) => theme.alert.modal.errorIcon};
  color: #ffb1b1;
  font-family: 'Open Sans';
  font-size: 9px;
  font-style: normal;
  font-weight: 400;
  line-height: 13px; /* 118.182% */
`;

export const AlertFormV3: React.FC<AlertModalFormProps> = ({
  multipleEdit,
  targetType,
  targetId,
}) => {
  const {
    register,
    formState: { errors },
    getValues,
    setValue,
    trigger,
    clearErrors,
    watch,
  } = useFormContext();
  const { ensembleFamilies } = useEnsembleModelsQuery();
  const { data: tagsByName } = useTagsByName();

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const filtersRef = useRef(null);

  // State for duration units for each field
  const [durationUnits, setDurationUnits] = useState<Record<string, TimeUnit>>({
    [`alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_area_threshold`]: 'mins',
    [`alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_eval_duration`]: 'mins',
    [`alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.duration`]: 'mins',
    [`alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.anomaly_duration`]: 'mins',
    [`alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_eval_duration`]: 'mins',
    [`alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.snooze_duration`]: 'mins',
    [`alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_area_threshold`]: 'mins',
    [`alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_eval_duration`]: 'mins',
    [`alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.duration`]: 'mins',
    [`alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.exceedance_duration`]: 'mins',
    [`alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_eval_duration`]: 'mins',
    [`alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.snooze_duration`]: 'mins',
  });

  const toggleViewActions = () => setIsFiltersOpen((prevState) => !prevState);
  useClickOutside(filtersRef, () => setIsFiltersOpen(false));

  // Get UOM from tag data when targetType is 'tag'
  const tagUOM = useMemo(() => {
    if (targetType === 'tag' && targetId && tagsByName) {
      const tag = tagsByName[targetId as string];
      return tag?.uom || '';
    }
    return '';
  }, [targetType, targetId, tagsByName]);

  const handleOnOptionChange = (option: Ensemble) => {
    setValue('ensemble_family_id', option.family_id, { shouldDirty: true, shouldTouch: true });
    setIsFiltersOpen(false);
  };

  const handleDurationUnitChange = (fieldName: string, newUnit: TimeUnit) => {
    // Update the unit state
    setDurationUnits((prev) => ({
      ...prev,
      [fieldName]: newUnit,
    }));
  };

  const getDisplayValue = (fieldName: string): number | undefined => {
    const formValue = watch(fieldName);
    if (formValue === null || formValue === undefined) {
      return undefined;
    }

    const unit = durationUnits[fieldName] || 'mins';

    // Convert from minutes (stored value) to display unit
    if (unit === 'hrs') {
      return formValue / 60;
    }
    return formValue;
  };

  const alertEnsembleFamilyId = watch('ensemble_family_id');

  const alertEnsemble = useMemo(() => {
    return ensembleFamilies.find((ef) => ef.family_id === alertEnsembleFamilyId);
  }, [alertEnsembleFamilyId, ensembleFamilies]);

  return (
    <EditAlertV2>
      {errors['apiError']?.message && (
        <InfoCard type="error">
          <Icon className="icon" name={ICONS.SystemDown} />
          <span data-testid="alert-error">{errors['apiError']?.message}</span>
        </InfoCard>
      )}
      <AlertConditionsWrapper>
        <HeaderWithToggle>
          <span>Alert fires for {getValues('target_name')} when...</span>
          <ToggleWrapper>
            <span>Turn alert on/off</span>
            <Switch
              selected={!watch('disabled')}
              onClick={(e, selected) => {
                setValue('disabled', !selected, {
                  shouldDirty: true,
                  shouldTouch: true,
                });
                trigger('disabled');
              }}
              dataTestId={'alerts_form'}
            />
          </ToggleWrapper>
        </HeaderWithToggle>
        <DisableWrapper disabled={watch('disabled')}>
          <AlertConditionsRow>
            <AlertConditionsColumn>
              <div className="alert-config anomalies">
                <div className="section-header">
                  <div className="checkbox">
                    <span>
                      {
                        AlertConditionsLabel[
                          AlertConditions.ANOMALIES_DETECTED.toUpperCase() as keyof typeof AlertConditionsLabel
                        ]
                      }
                    </span>
                  </div>
                  <div className="ensemble">
                    <span>Ensemble family</span>

                    <Dropdown.Root onClose={() => toggleViewActions} isOpen={isFiltersOpen}>
                      <Dropdown.Trigger
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        style={{ padding: 0 }}
                      >
                        <MenuButton
                          data-testid="alert-family-browser-filter"
                          buttonSize="small"
                          buttonType="menu"
                          onClick={toggleViewActions}
                          aria-haspopup="listbox"
                          aria-expanded={isFiltersOpen}
                        >
                          {alertEnsemble?.family_name || ''}
                        </MenuButton>
                      </Dropdown.Trigger>
                      <Dropdown.Options position="right" open={isFiltersOpen} ref={filtersRef}>
                        {ensembleFamilies?.map((option) => {
                          const isSelected = alertEnsemble?.family_id === option.family_id;
                          return (
                            <Dropdown.Item
                              key={option.family_id}
                              data-testid={`ensemble_family_${option.family_id}`}
                              onClick={() => {
                                handleOnOptionChange(option);
                              }}
                            >
                              {isSelected && (
                                <Icon
                                  data-testid={`${option.family_id}-checkmark`}
                                  name={ICONS.CheckmarkDefault}
                                />
                              )}
                              <span style={{ paddingLeft: isSelected ? 0 : 20 }}>
                                {option.family_name}
                              </span>
                            </Dropdown.Item>
                          );
                        })}
                      </Dropdown.Options>
                    </Dropdown.Root>
                  </div>
                </div>
                <div className="alert-conditions">
                  <div className="row">
                    <div className="selector">
                      <div className="checkbox">
                        <FormCheckbox
                          dataTestId={`${AlertConditions.ANOMALIES_DETECTED}-oob_area_selected-checkbox`}
                          {...register(
                            `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_area_selected`,
                            {
                              validate: (value) => {
                                if (multipleEdit) {
                                  return true;
                                }
                                if (!value) {
                                  clearErrors([
                                    `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_area_threshold`,
                                    `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_eval_duration`,
                                  ]);

                                  // Clear snooze duration error when ALL anomaly checkboxes are unchecked
                                  const durationSelected = getValues(
                                    `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.selected`,
                                  );
                                  const anomalyOscillationSelected = getValues(
                                    `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_selected`,
                                  );

                                  // If ALL anomaly checkboxes are unchecked, clear snooze error
                                  if (!value && !durationSelected && !anomalyOscillationSelected) {
                                    clearErrors([
                                      `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.snooze_duration`,
                                    ]);
                                  }
                                }
                              },
                              onChange: () => {
                                // Clear the general alert_conditions error when any checkbox is selected
                                clearErrors('alert_conditions');
                              },
                            },
                          )}
                          checked={watch(
                            `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_area_selected`,
                          )}
                          disabled={errors['apiError']?.message}
                        />
                        <span>Anomaly area</span>
                      </div>
                    </div>
                    <div className="group">
                      <div className="button-label">
                        <div className="label">Area out of bounds</div>
                        <div className="input-group">
                          <TextInput
                            type="number"
                            data-testid={`${AlertConditions.ANOMALIES_DETECTED}-oob_area_threshold`}
                            value={getDisplayValue(
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_area_threshold`,
                            )}
                            className="duration"
                            label=""
                            placeholder=""
                            {...register(
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_area_threshold`,
                              {
                                setValueAs: (v) => {
                                  if (!v) return null;
                                  const numericValue = parseInt(v, 10);
                                  const unit =
                                    durationUnits[
                                      `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_area_threshold`
                                    ] || 'mins';
                                  // Convert to minutes for storage
                                  return unit === 'hrs' ? numericValue * 60 : numericValue;
                                },
                                validate: (value) => {
                                  if (parseInt(value, 10) <= 0) {
                                    return 'Must be greater than 0';
                                  }
                                  if (multipleEdit) {
                                    return true;
                                  }
                                  if (
                                    getValues(
                                      `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_area_selected`,
                                    ) &&
                                    !value
                                  ) {
                                    return 'Required field';
                                  }
                                  return true;
                                },
                              },
                            )}
                          />
                          {targetType === 'tag' && (
                            <MenuButton
                              data-testid="anomaly-oob_area_threshold-uom"
                              buttonSize="small"
                              buttonType="menu"
                            >
                              {tagUOM || ''}
                            </MenuButton>
                          )}
                        </div>
                        <ErrorMessage>
                          {`alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_area_threshold`
                            .split('.')
                            .reduce((v, k) => (v || {})[k], errors)?.message || ''}
                        </ErrorMessage>
                      </div>
                      <div className="over">over</div>
                      <div className="button-label">
                        <div className="label"></div>
                        <div className="input-group">
                          <TextInput
                            type="number"
                            data-testid={`${AlertConditions.ANOMALIES_DETECTED}-oob_eval_duration`}
                            value={getDisplayValue(
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_eval_duration`,
                            )}
                            className="duration"
                            label=""
                            placeholder=""
                            {...register(
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_eval_duration`,
                              {
                                setValueAs: (v) => {
                                  if (!v) return null;
                                  const numericValue = parseInt(v, 10);
                                  const unit =
                                    durationUnits[
                                      `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_eval_duration`
                                    ] || 'mins';
                                  // Convert to minutes for storage
                                  return unit === 'hrs' ? numericValue * 60 : numericValue;
                                },
                                validate: (value) => {
                                  if (parseInt(value, 10) <= 0) {
                                    return 'Must be greater than 0';
                                  }
                                  if (multipleEdit) {
                                    return true;
                                  }
                                  if (
                                    getValues(
                                      `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_area_selected`,
                                    ) &&
                                    !value
                                  ) {
                                    return 'Required field';
                                  }
                                  return true;
                                },
                              },
                            )}
                          />
                          <DurationDropdown
                            currentUnit={
                              durationUnits[
                                `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_eval_duration`
                              ] || 'mins'
                            }
                            onUnitChange={(unit) =>
                              handleDurationUnitChange(
                                `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_eval_duration`,
                                unit,
                              )
                            }
                            testId="oob_eval_duration-button"
                          />
                        </div>
                        <ErrorMessage>
                          {`alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_eval_duration`
                            .split('.')
                            .reduce((v, k) => (v || {})[k], errors)?.message || ''}
                        </ErrorMessage>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="selector">
                      <div className="checkbox">
                        <FormCheckbox
                          dataTestId={`${AlertConditions.ANOMALIES_DETECTED}-duration_over_time_selected-checkbox`}
                          {...register(
                            `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.selected`,
                            {
                              validate: (value) => {
                                if (multipleEdit) {
                                  return true;
                                }
                                if (!value) {
                                  clearErrors([
                                    `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.duration`,
                                    `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.anomaly_duration`,
                                  ]);
                                }
                              },
                              onChange: () => {
                                // Clear the general alert_conditions error when any checkbox is selected
                                clearErrors('alert_conditions');
                              },
                            },
                          )}
                          checked={watch(
                            `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.selected`,
                          )}
                          disabled={errors['apiError']?.message}
                          customOnChange={() => {
                            trigger([
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.selected`,
                            ]);
                            // Clear snooze duration error when ALL anomaly checkboxes are unchecked
                            const anomalyAreaSelected = getValues(
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_area_selected`,
                            );
                            const anomalyOscillationSelected = getValues(
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_selected`,
                            );
                            const currentSelected = getValues(
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.selected`,
                            );

                            // If ALL anomaly checkboxes are unchecked, clear snooze error
                            if (
                              !currentSelected &&
                              !anomalyAreaSelected &&
                              !anomalyOscillationSelected
                            ) {
                              clearErrors([
                                `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.snooze_duration`,
                              ]);
                            }
                          }}
                        />
                        <span>Duration over time</span>
                      </div>
                    </div>
                    <div className="group">
                      <div className="button-label">
                        <div className="label">Time anomalous</div>
                        <div className="input-group">
                          <TextInput
                            type="number"
                            data-testid={`${AlertConditions.ANOMALIES_DETECTED}-anomaly_duration`}
                            value={getDisplayValue(
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.anomaly_duration`,
                            )}
                            className="duration"
                            label=""
                            placeholder=""
                            {...register(
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.anomaly_duration`,
                              {
                                setValueAs: (v) => {
                                  if (!v) return null;
                                  const numericValue = parseInt(v, 10);
                                  const unit =
                                    durationUnits[
                                      `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.anomaly_duration`
                                    ] || 'mins';
                                  // Convert to minutes for storage
                                  return unit === 'hrs' ? numericValue * 60 : numericValue;
                                },
                                validate: (value) => {
                                  if (parseInt(value, 10) <= 0) {
                                    return 'Must be greater than 0';
                                  }
                                  if (multipleEdit) {
                                    return true;
                                  }
                                  if (
                                    getValues(
                                      `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.selected`,
                                    ) &&
                                    !value
                                  ) {
                                    return 'Required field';
                                  }
                                  return true;
                                },
                              },
                            )}
                          />
                          <DurationDropdown
                            currentUnit={
                              durationUnits[
                                `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.anomaly_duration`
                              ] || 'mins'
                            }
                            onUnitChange={(unit) =>
                              handleDurationUnitChange(
                                `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.anomaly_duration`,
                                unit,
                              )
                            }
                            testId="anomaly_duration-button"
                          />
                        </div>
                        <ErrorMessage>
                          {`alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.anomaly_duration`
                            .split('.')
                            .reduce((v, k) => (v || {})[k], errors)?.message || ''}
                        </ErrorMessage>
                      </div>
                      <div className="over">over</div>
                      <div className="button-label">
                        <div className="label"></div>
                        <div className="input-group">
                          <TextInput
                            type="number"
                            data-testid={`${AlertConditions.ANOMALIES_DETECTED}-duration`}
                            value={getDisplayValue(
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.duration`,
                            )}
                            className="duration"
                            label=""
                            placeholder=""
                            {...register(
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.duration`,
                              {
                                setValueAs: (v) => {
                                  if (!v) return null;
                                  const numericValue = parseInt(v, 10);
                                  const unit =
                                    durationUnits[
                                      `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.duration`
                                    ] || 'mins';
                                  // Convert to minutes for storage
                                  return unit === 'hrs' ? numericValue * 60 : numericValue;
                                },
                                validate: (value) => {
                                  if (parseInt(value, 10) <= 0) {
                                    return 'Must be greater than 0';
                                  }
                                  if (multipleEdit) {
                                    return true;
                                  }
                                  if (
                                    getValues(
                                      `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.selected`,
                                    ) &&
                                    !value
                                  ) {
                                    return 'Required field';
                                  }
                                  return true;
                                },
                              },
                            )}
                          />
                          <DurationDropdown
                            currentUnit={
                              durationUnits[
                                `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.duration`
                              ] || 'mins'
                            }
                            onUnitChange={(unit) =>
                              handleDurationUnitChange(
                                `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.duration`,
                                unit,
                              )
                            }
                            testId="duration-button"
                          />
                        </div>
                        <ErrorMessage>
                          {`alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.duration`
                            .split('.')
                            .reduce((v, k) => (v || {})[k], errors)?.message || ''}
                        </ErrorMessage>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="selector">
                      <div className="checkbox">
                        <FormCheckbox
                          dataTestId={`${AlertConditions.ANOMALIES_DETECTED}-oscillation_selected-checkbox`}
                          {...register(
                            `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_selected`,
                            {
                              validate: (value) => {
                                if (multipleEdit) {
                                  return true;
                                }
                                if (!value) {
                                  clearErrors([
                                    `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_count_threshold`,
                                    `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_eval_duration`,
                                  ]);
                                }
                              },
                              onChange: () => {
                                // Clear the general alert_conditions error when any checkbox is selected
                                clearErrors('alert_conditions');
                              },
                            },
                          )}
                          checked={watch(
                            `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_selected`,
                          )}
                          disabled={errors['apiError']?.message}
                          customOnChange={() => {
                            trigger([
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_selected`,
                            ]);
                            // Clear snooze duration error when ALL anomaly checkboxes are unchecked
                            const anomalyAreaSelected = getValues(
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_area_selected`,
                            );
                            const durationSelected = getValues(
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.selected`,
                            );
                            const currentOscillationSelected = getValues(
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_selected`,
                            );

                            // If ALL anomaly checkboxes are unchecked, clear snooze error
                            if (
                              !currentOscillationSelected &&
                              !anomalyAreaSelected &&
                              !durationSelected
                            ) {
                              clearErrors([
                                `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.snooze_duration`,
                              ]);
                            }
                          }}
                        />
                        <span>Oscillation</span>
                      </div>
                    </div>
                    <div className="group">
                      <div className="button-label">
                        <div className="label">Count</div>
                        <div className="input-group">
                          <TextInput
                            type="number"
                            data-testid={`${AlertConditions.ANOMALIES_DETECTED}-oscillation_count_threshold`}
                            value={watch(
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_count_threshold`,
                            )}
                            className="duration"
                            label=""
                            placeholder=""
                            // errorId={`input--${alert?.alert_view_summary?.name}-anomalies-snooze-duration-error`}
                            {...register(
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_count_threshold`,
                              {
                                setValueAs: (v) => {
                                  if (!v) return null;
                                  return parseInt(v, 10);
                                },
                                validate: (value) => {
                                  if (parseInt(value, 10) <= 0) {
                                    return 'Must be greater than 0';
                                  }
                                  if (multipleEdit) {
                                    return true;
                                  }
                                  if (
                                    getValues(
                                      `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_selected`,
                                    ) &&
                                    !value
                                  ) {
                                    return 'Required field';
                                  }
                                  return true;
                                },
                              },
                            )}
                          />
                        </div>
                        <ErrorMessage>
                          {`alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_count_threshold`
                            .split('.')
                            .reduce((v, k) => (v || {})[k], errors)?.message || ''}
                        </ErrorMessage>
                      </div>
                      <div className="over">over</div>
                      <div className="button-label">
                        <div className="label"></div>
                        <div className="input-group">
                          <TextInput
                            type="number"
                            data-testid={`${AlertConditions.ANOMALIES_DETECTED}-oscillation_eval_duration`}
                            value={getDisplayValue(
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_eval_duration`,
                            )}
                            className="duration"
                            label=""
                            placeholder=""
                            {...register(
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_eval_duration`,
                              {
                                setValueAs: (v) => {
                                  if (!v) return null;
                                  const numericValue = parseInt(v, 10);
                                  const unit =
                                    durationUnits[
                                      `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_eval_duration`
                                    ] || 'mins';
                                  // Convert to minutes for storage
                                  return unit === 'hrs' ? numericValue * 60 : numericValue;
                                },
                                validate: (value) => {
                                  if (parseInt(value, 10) <= 0) {
                                    return 'Must be greater than 0';
                                  }
                                  if (multipleEdit) {
                                    return true;
                                  }
                                  if (
                                    getValues(
                                      `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_selected`,
                                    ) &&
                                    !value
                                  ) {
                                    return 'Required field';
                                  }
                                  return true;
                                },
                              },
                            )}
                          />
                          <DurationDropdown
                            currentUnit={
                              durationUnits[
                                `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_eval_duration`
                              ] || 'mins'
                            }
                            onUnitChange={(unit) =>
                              handleDurationUnitChange(
                                `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_eval_duration`,
                                unit,
                              )
                            }
                            testId="oscillation_eval_duration-button"
                          />
                        </div>
                        <ErrorMessage>
                          {`alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_eval_duration`
                            .split('.')
                            .reduce((v, k) => (v || {})[k], errors)?.message || ''}
                        </ErrorMessage>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="button-label">
                      <div className="label">
                        <SnoozeTitle />
                      </div>
                      <div className="input-group">
                        <TextInput
                          type="number"
                          data-testid={`${AlertConditions.ANOMALIES_DETECTED}-edit-snooze`}
                          value={getDisplayValue(
                            `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.snooze_duration`,
                          )}
                          className="duration"
                          label=""
                          placeholder=""
                          {...register(
                            `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.snooze_duration`,
                            {
                              setValueAs: (v) => {
                                if (!v) return null;
                                const numericValue = parseInt(v, 10);
                                const unit =
                                  durationUnits[
                                    `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.snooze_duration`
                                  ] || 'mins';
                                // Convert to minutes for storage
                                return unit === 'hrs' ? numericValue * 60 : numericValue;
                              },
                              validate: (value) => {
                                if (parseInt(value, 10) <= 0) {
                                  return 'Must be greater than 0';
                                }
                                if (multipleEdit) {
                                  return true;
                                }

                                const selected = getValues(
                                  `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.selected`,
                                );
                                const anomalyAreaSelected = getValues(
                                  `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oob_area_selected`,
                                );
                                const anomalyOscillationSelected = getValues(
                                  `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.oscillation_selected`,
                                );

                                // If none of the conditions are selected, clear any error
                                if (
                                  !selected &&
                                  !anomalyAreaSelected &&
                                  !anomalyOscillationSelected
                                ) {
                                  return true;
                                }

                                // If any condition is selected but no value provided, show error
                                if (
                                  (selected || anomalyAreaSelected || anomalyOscillationSelected) &&
                                  !value
                                ) {
                                  return 'Required field';
                                }

                                return true;
                              },
                            },
                          )}
                        />
                        <DurationDropdown
                          currentUnit={
                            durationUnits[
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.snooze_duration`
                            ] || 'mins'
                          }
                          onUnitChange={(unit) =>
                            handleDurationUnitChange(
                              `alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.snooze_duration`,
                              unit,
                            )
                          }
                          testId="snooze_duration-button"
                        />
                      </div>
                      <ErrorMessage>
                        {`alert_conditions.${AlertConditions.ANOMALIES_DETECTED}.snooze_duration`
                          .split('.')
                          .reduce((v, k) => (v || {})[k], errors)?.message || ''}
                      </ErrorMessage>
                    </div>
                  </div>
                </div>
              </div>
            </AlertConditionsColumn>
            <AlertConditionsColumn>
              <div className="alert-config anomalies">
                <div className="section-header">
                  <div className="checkbox">
                    <span>
                      {
                        AlertConditionsLabel[
                          AlertConditions.IOW_LIMIT_EXCEEDED.toUpperCase() as keyof typeof AlertConditionsLabel
                        ]
                      }
                    </span>
                  </div>
                </div>
                <div className="alert-conditions">
                  <div className="row">
                    <div className="selector">
                      <div className="checkbox">
                        <FormCheckbox
                          dataTestId={`${AlertConditions.IOW_LIMIT_EXCEEDED}-oob_area_selected-checkbox`}
                          {...register(
                            `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_area_selected`,
                            {
                              validate: (value) => {
                                if (multipleEdit) {
                                  return true;
                                }
                                if (!value) {
                                  clearErrors([
                                    `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_area_threshold`,
                                    `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_eval_duration`,
                                  ]);

                                  // Clear snooze duration error when ALL IOW checkboxes are unchecked
                                  const durationSelected = getValues(
                                    `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.selected`,
                                  );
                                  const limitOscillationSelected = getValues(
                                    `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_selected`,
                                  );

                                  // If ALL IOW checkboxes are unchecked, clear snooze error
                                  if (!value && !durationSelected && !limitOscillationSelected) {
                                    clearErrors([
                                      `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.snooze_duration`,
                                    ]);
                                  }
                                }
                              },
                              onChange: () => {
                                // Clear the general alert_conditions error when any checkbox is selected
                                clearErrors('alert_conditions');
                              },
                            },
                          )}
                          checked={watch(
                            `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_area_selected`,
                          )}
                          disabled={errors['apiError']?.message}
                        />
                        <span>Limit area</span>
                      </div>
                    </div>
                    <div className="group">
                      <div className="button-label">
                        <div className="label">Area out of bounds</div>
                        <div className="input-group">
                          <TextInput
                            type="number"
                            data-testid={`${AlertConditions.IOW_LIMIT_EXCEEDED}-oob_area_threshold`}
                            value={getDisplayValue(
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_area_threshold`,
                            )}
                            className="duration"
                            label=""
                            placeholder=""
                            {...register(
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_area_threshold`,
                              {
                                setValueAs: (v) => {
                                  if (!v) return null;
                                  const numericValue = parseInt(v, 10);
                                  const unit =
                                    durationUnits[
                                      `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_area_threshold`
                                    ] || 'mins';
                                  // Convert to minutes for storage
                                  return unit === 'hrs' ? numericValue * 60 : numericValue;
                                },
                                validate: (value) => {
                                  if (parseInt(value, 10) <= 0) {
                                    return 'Must be greater than 0';
                                  }
                                  if (multipleEdit) {
                                    return true;
                                  }
                                  if (
                                    getValues(
                                      `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_area_selected`,
                                    ) &&
                                    !value
                                  ) {
                                    return 'Required field';
                                  }
                                  return true;
                                },
                              },
                            )}
                          />
                          {targetType === 'tag' && (
                            <MenuButton
                              data-testid="limit-oob_area_threshold-uom"
                              buttonSize="small"
                              buttonType="menu"
                            >
                              {tagUOM || ''}
                            </MenuButton>
                          )}
                        </div>
                        <ErrorMessage>
                          {`alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_area_threshold`
                            .split('.')
                            .reduce((v, k) => (v || {})[k], errors)?.message || ''}
                        </ErrorMessage>
                      </div>
                      <div className="over">over</div>
                      <div className="button-label">
                        <div className="label"></div>
                        <div className="input-group">
                          <TextInput
                            type="number"
                            data-testid={`${AlertConditions.IOW_LIMIT_EXCEEDED}-oob_eval_duration`}
                            value={getDisplayValue(
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_eval_duration`,
                            )}
                            className="duration"
                            label=""
                            placeholder=""
                            {...register(
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_eval_duration`,
                              {
                                setValueAs: (v) => {
                                  if (!v) return null;
                                  const numericValue = parseInt(v, 10);
                                  const unit =
                                    durationUnits[
                                      `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_eval_duration`
                                    ] || 'mins';
                                  // Convert to minutes for storage
                                  return unit === 'hrs' ? numericValue * 60 : numericValue;
                                },
                                validate: (value) => {
                                  if (parseInt(value, 10) <= 0) {
                                    return 'Must be greater than 0';
                                  }
                                  if (multipleEdit) {
                                    return true;
                                  }
                                  if (
                                    getValues(
                                      `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_area_selected`,
                                    ) &&
                                    !value
                                  ) {
                                    return 'Required field';
                                  }
                                  return true;
                                },
                              },
                            )}
                          />
                          <DurationDropdown
                            currentUnit={
                              durationUnits[
                                `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_eval_duration`
                              ] || 'mins'
                            }
                            onUnitChange={(unit) =>
                              handleDurationUnitChange(
                                `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_eval_duration`,
                                unit,
                              )
                            }
                            testId="iow_oob_eval_duration-button"
                          />
                        </div>
                        <ErrorMessage>
                          {`alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_eval_duration`
                            .split('.')
                            .reduce((v, k) => (v || {})[k], errors)?.message || ''}
                        </ErrorMessage>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="selector">
                      <div className="checkbox">
                        <FormCheckbox
                          dataTestId={`${AlertConditions.IOW_LIMIT_EXCEEDED}-duration_over_time_selected-checkbox`}
                          {...register(
                            `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.selected`,
                            {
                              validate: (value) => {
                                if (multipleEdit) {
                                  return true;
                                }
                                if (!value) {
                                  clearErrors([
                                    `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.duration`,
                                    `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.exceedance_duration`,
                                  ]);
                                }
                              },
                              onChange: () => {
                                // Clear the general alert_conditions error when any checkbox is selected
                                clearErrors('alert_conditions');
                              },
                            },
                          )}
                          checked={watch(
                            `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.selected`,
                          )}
                          disabled={errors['apiError']?.message}
                          customOnChange={() => {
                            trigger([
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.selected`,
                            ]);
                            // Clear snooze duration error when ALL IOW checkboxes are unchecked
                            const limitAreaSelected = getValues(
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_area_selected`,
                            );
                            const limitOscillationSelected = getValues(
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_selected`,
                            );
                            const currentSelected = getValues(
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.selected`,
                            );

                            // If ALL IOW checkboxes are unchecked, clear snooze error
                            if (
                              !currentSelected &&
                              !limitAreaSelected &&
                              !limitOscillationSelected
                            ) {
                              clearErrors([
                                `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.snooze_duration`,
                              ]);
                            }
                          }}
                        />
                        <span>Duration over time</span>
                      </div>
                    </div>
                    <div className="group">
                      <div className="button-label">
                        <div className="label">Time anomalous</div>
                        <div className="input-group">
                          <TextInput
                            type="number"
                            data-testid={`${AlertConditions.IOW_LIMIT_EXCEEDED}-exceedance_duration`}
                            value={getDisplayValue(
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.exceedance_duration`,
                            )}
                            className="duration"
                            label=""
                            placeholder=""
                            {...register(
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.exceedance_duration`,
                              {
                                setValueAs: (v) => {
                                  if (!v) return null;
                                  const numericValue = parseInt(v, 10);
                                  const unit =
                                    durationUnits[
                                      `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.exceedance_duration`
                                    ] || 'mins';
                                  // Convert to minutes for storage
                                  return unit === 'hrs' ? numericValue * 60 : numericValue;
                                },
                                validate: (value) => {
                                  if (parseInt(value, 10) <= 0) {
                                    return 'Must be greater than 0';
                                  }
                                  if (multipleEdit) {
                                    return true;
                                  }
                                  if (
                                    getValues(
                                      `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.selected`,
                                    ) &&
                                    !value
                                  ) {
                                    return 'Required field';
                                  }
                                  return true;
                                },
                              },
                            )}
                          />
                          <DurationDropdown
                            currentUnit={
                              durationUnits[
                                `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.exceedance_duration`
                              ] || 'mins'
                            }
                            onUnitChange={(unit) =>
                              handleDurationUnitChange(
                                `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.exceedance_duration`,
                                unit,
                              )
                            }
                            testId="iow_exceedance_duration-button"
                          />
                        </div>
                        <ErrorMessage>
                          {`alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.exceedance_duration`
                            .split('.')
                            .reduce((v, k) => (v || {})[k], errors)?.message || ''}
                        </ErrorMessage>
                      </div>
                      <div className="over">over</div>
                      <div className="button-label">
                        <div className="label"></div>
                        <div className="input-group">
                          <TextInput
                            type="number"
                            data-testid={`${AlertConditions.IOW_LIMIT_EXCEEDED}-duration`}
                            value={getDisplayValue(
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.duration`,
                            )}
                            className="duration"
                            label=""
                            placeholder=""
                            {...register(
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.duration`,
                              {
                                setValueAs: (v) => {
                                  if (!v) return null;
                                  const numericValue = parseInt(v, 10);
                                  const unit =
                                    durationUnits[
                                      `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.duration`
                                    ] || 'mins';
                                  // Convert to minutes for storage
                                  return unit === 'hrs' ? numericValue * 60 : numericValue;
                                },
                                validate: (value) => {
                                  if (parseInt(value, 10) <= 0) {
                                    return 'Must be greater than 0';
                                  }
                                  if (multipleEdit) {
                                    return true;
                                  }
                                  if (
                                    getValues(
                                      `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.selected`,
                                    ) &&
                                    !value
                                  ) {
                                    return 'Required field';
                                  }
                                  return true;
                                },
                              },
                            )}
                          />
                          <DurationDropdown
                            currentUnit={
                              durationUnits[
                                `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.duration`
                              ] || 'mins'
                            }
                            onUnitChange={(unit) =>
                              handleDurationUnitChange(
                                `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.duration`,
                                unit,
                              )
                            }
                            testId="iow_duration-button"
                          />
                        </div>
                        <ErrorMessage>
                          {`alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.duration`
                            .split('.')
                            .reduce((v, k) => (v || {})[k], errors)?.message || ''}
                        </ErrorMessage>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="selector">
                      <div className="checkbox">
                        <FormCheckbox
                          dataTestId={`${AlertConditions.IOW_LIMIT_EXCEEDED}-oscillation_selected-checkbox`}
                          {...register(
                            `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_selected`,
                            {
                              validate: (value) => {
                                if (multipleEdit) {
                                  return true;
                                }
                                if (!value) {
                                  clearErrors([
                                    `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_count_threshold`,
                                    `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_eval_duration`,
                                  ]);
                                }
                              },
                              onChange: () => {
                                // Clear the general alert_conditions error when any checkbox is selected
                                clearErrors('alert_conditions');
                              },
                            },
                          )}
                          checked={watch(
                            `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_selected`,
                          )}
                          disabled={errors['apiError']?.message}
                          customOnChange={() => {
                            trigger([
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_selected`,
                            ]);
                            // Clear snooze duration error when ALL IOW checkboxes are unchecked
                            const limitAreaSelected = getValues(
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_area_selected`,
                            );
                            const durationSelected = getValues(
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.selected`,
                            );
                            const currentOscillationSelected = getValues(
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_selected`,
                            );

                            // If ALL IOW checkboxes are unchecked, clear snooze error
                            if (
                              !currentOscillationSelected &&
                              !limitAreaSelected &&
                              !durationSelected
                            ) {
                              clearErrors([
                                `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.snooze_duration`,
                              ]);
                            }
                          }}
                        />
                        <span>Oscillation</span>
                      </div>
                    </div>
                    <div className="group">
                      <div className="button-label">
                        <div className="label">Count</div>
                        <div className="input-group">
                          <TextInput
                            type="number"
                            data-testid={`${AlertConditions.IOW_LIMIT_EXCEEDED}-oscillation_count_threshold`}
                            value={watch(
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_count_threshold`,
                            )}
                            className="duration"
                            label=""
                            placeholder=""
                            {...register(
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_count_threshold`,
                              {
                                setValueAs: (v) => {
                                  if (!v) return null;
                                  return parseInt(v, 10);
                                },
                                validate: (value) => {
                                  if (parseInt(value, 10) <= 0) {
                                    return 'Must be greater than 0';
                                  }
                                  if (multipleEdit) {
                                    return true;
                                  }
                                  if (
                                    getValues(
                                      `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_selected`,
                                    ) &&
                                    !value
                                  ) {
                                    return 'Required field';
                                  }
                                  return true;
                                },
                              },
                            )}
                          />
                        </div>
                        <ErrorMessage>
                          {`alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_count_threshold`
                            .split('.')
                            .reduce((v, k) => (v || {})[k], errors)?.message || ''}
                        </ErrorMessage>
                      </div>
                      <div className="over">over</div>
                      <div className="button-label">
                        <div className="label"></div>
                        <div className="input-group">
                          <TextInput
                            type="number"
                            data-testid={`${AlertConditions.IOW_LIMIT_EXCEEDED}-oscillation_eval_duration`}
                            value={watch(
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_eval_duration`,
                            )}
                            className="duration"
                            label=""
                            placeholder=""
                            {...register(
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_eval_duration`,
                              {
                                setValueAs: (v) => {
                                  if (!v) return null;
                                  return parseInt(v, 10);
                                },
                                validate: (value) => {
                                  if (parseInt(value, 10) <= 0) {
                                    return 'Must be greater than 0';
                                  }
                                  if (multipleEdit) {
                                    return true;
                                  }
                                  if (
                                    getValues(
                                      `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_selected`,
                                    ) &&
                                    !value
                                  ) {
                                    return 'Required field';
                                  }
                                  return true;
                                },
                              },
                            )}
                          />
                          <DurationDropdown
                            currentUnit={
                              durationUnits[
                                `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_eval_duration`
                              ] || 'mins'
                            }
                            onUnitChange={(unit) =>
                              handleDurationUnitChange(
                                `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_eval_duration`,
                                unit,
                              )
                            }
                            testId="iow_oscillation_eval_duration-button"
                          />
                        </div>
                        <ErrorMessage>
                          {`alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_eval_duration`
                            .split('.')
                            .reduce((v, k) => (v || {})[k], errors)?.message || ''}
                        </ErrorMessage>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="button-label">
                      <div className="label">
                        <SnoozeTitle />
                      </div>
                      <div className="input-group">
                        <TextInput
                          type="number"
                          data-testid={`${AlertConditions.IOW_LIMIT_EXCEEDED}-edit-snooze`}
                          value={getDisplayValue(
                            `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.snooze_duration`,
                          )}
                          className="duration"
                          label=""
                          placeholder=""
                          {...register(
                            `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.snooze_duration`,
                            {
                              setValueAs: (v) => {
                                if (!v) return null;
                                const numericValue = parseInt(v, 10);
                                const unit =
                                  durationUnits[
                                    `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.snooze_duration`
                                  ] || 'mins';
                                // Convert to minutes for storage
                                return unit === 'hrs' ? numericValue * 60 : numericValue;
                              },
                              validate: (value) => {
                                if (parseInt(value, 10) <= 0) {
                                  return 'Must be greater than 0';
                                }
                                if (multipleEdit) {
                                  return true;
                                }

                                const selected = getValues(
                                  `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.selected`,
                                );
                                const limitAreaSelected = getValues(
                                  `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oob_area_selected`,
                                );
                                const limitOscillationSelected = getValues(
                                  `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.oscillation_selected`,
                                );

                                // If none of the conditions are selected, clear any error
                                if (!selected && !limitAreaSelected && !limitOscillationSelected) {
                                  return true;
                                }

                                // If any condition is selected but no value provided, show error
                                if (
                                  (selected || limitAreaSelected || limitOscillationSelected) &&
                                  !value
                                ) {
                                  return 'Required field';
                                }

                                return true;
                              },
                            },
                          )}
                        />
                        <DurationDropdown
                          currentUnit={
                            durationUnits[
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.snooze_duration`
                            ] || 'mins'
                          }
                          onUnitChange={(unit) =>
                            handleDurationUnitChange(
                              `alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.snooze_duration`,
                              unit,
                            )
                          }
                          testId="iow_snooze_duration-button"
                        />
                      </div>
                      <ErrorMessage>
                        {`alert_conditions.${AlertConditions.IOW_LIMIT_EXCEEDED}.snooze_duration`
                          .split('.')
                          .reduce((v, k) => (v || {})[k], errors)?.message || ''}
                      </ErrorMessage>
                    </div>
                  </div>
                </div>
              </div>
            </AlertConditionsColumn>
          </AlertConditionsRow>
          {errors.alert_conditions?.message && (
            <ErrorMessage style={{ paddingLeft: '12px', fontSize: '11px' }}>
              {errors.alert_conditions?.message}
            </ErrorMessage>
          )}
        </DisableWrapper>
      </AlertConditionsWrapper>
      <DisableWrapper disabled={watch('disabled')}>
        <DeliverChannelsWrapper>
          <DeliverChannelsHeader>Deliver Channels</DeliverChannelsHeader>
          <DeliverChannels>
            <div className="row">
              <div className="column">
                <AlertDashboardPicker />
              </div>
              <div className="column">
                <TeamsWebhook />
              </div>
            </div>
            <div className="row">
              <div className="column">
                <AlertEmails />
              </div>
            </div>
          </DeliverChannels>
        </DeliverChannelsWrapper>
      </DisableWrapper>
    </EditAlertV2>
  );
};
